
import { SleepEvent, SleepPeriod } from './types';
import { startOfDay, endOfDay, isAfter } from 'date-fns';

export function reconstructSleepPeriods(events: SleepEvent[]): SleepPeriod[] {
  const periods: SleepPeriod[] = [];
  if (!events || events.length === 0) return periods;

  // Filter out events with invalid timestamps first
  const eventsWithValidTimestamps = events.filter(event => {
    if (!event.time) return false;
    try {
      const date = event.time.toDate();
      return !isNaN(date.getTime());
    } catch (error) {
      console.warn('Error converting timestamp in timeline reconstruction:', event, error);
      return false;
    }
  });

  // Sort events by time to ensure proper order
  const sortedEvents = [...eventsWithValidTimestamps].sort((a, b) => a.time.toDate().getTime() - b.time.toDate().getTime());
  
  // Filter out any invalid events
  const validEvents = sortedEvents.filter(event => event.time && event.type);

  let currentNightSleep: Partial<SleepPeriod> | null = null;
  let currentNap: Partial<SleepPeriod> | null = null;

  for (const event of validEvents) {
    let eventTime: Date;
    try {
      eventTime = event.time.toDate();
      if (isNaN(eventTime.getTime())) {
        console.warn('Invalid date in timeline reconstruction:', event);
        continue; // Skip this event
      }
    } catch (error) {
      console.warn('Error converting timestamp in timeline reconstruction:', event, error);
      continue; // Skip this event
    }
    switch (event.type) {
      case 'bedtime':
        if (currentNightSleep?.isComplete === false) {
           // Incomplete period, finalize it
           periods.push({ ...currentNightSleep, endTime: eventTime } as SleepPeriod);
        }
        currentNightSleep = {
          type: 'night',
          startTime: eventTime,
          isComplete: false,
          wakeUps: [],
          feeds: [],
        };
        break;

      case 'wake':
        if (currentNightSleep?.isComplete === false) {
          currentNightSleep.endTime = eventTime;
          currentNightSleep.isComplete = true;

          if (currentNightSleep.startTime && currentNightSleep.endTime) {
            const midnight = startOfDay(currentNightSleep.endTime);
            // If sleep spans across midnight, split it into two periods
            if (currentNightSleep.startTime < midnight && isAfter(currentNightSleep.endTime, midnight)) {
              const firstPart = { 
                ...currentNightSleep, 
                endTime: endOfDay(currentNightSleep.startTime),
                wakeUps: currentNightSleep.wakeUps?.filter(w => w.time < midnight),
                feeds: currentNightSleep.feeds?.filter(f => f.time < midnight),
              } as SleepPeriod;
              
              const secondPart = { 
                ...currentNightSleep, 
                startTime: startOfDay(currentNightSleep.endTime),
                wakeUps: currentNightSleep.wakeUps?.filter(w => w.time >= midnight),
                feeds: currentNightSleep.feeds?.filter(f => f.time >= midnight),
              } as SleepPeriod;

              periods.push(firstPart, secondPart);
            } else {
              periods.push(currentNightSleep as SleepPeriod);
            }
          }

          currentNightSleep = null;
        }
        break;

      case 'nap_start':
         if (currentNap?.isComplete === false) {
            periods.push({ ...currentNap, endTime: eventTime } as SleepPeriod);
         }
        currentNap = {
          type: 'nap',
          startTime: eventTime,
          isComplete: false,
          wakeUps: [],
          feeds: [],
        };
        break;

      case 'nap_end':
        if (currentNap?.isComplete === false) {
          currentNap.endTime = eventTime;
          currentNap.isComplete = true;
          periods.push(currentNap as SleepPeriod);
          currentNap = null;
        }
        break;

      case 'nap': {
        const napDuration = event.duration && event.duration > 0 ? event.duration : 60;
        const endTime = new Date(eventTime.getTime() + napDuration * 60 * 1000);
        periods.push({
          type: 'nap',
          startTime: eventTime,
          endTime,
          isComplete: true,
          wakeUps: [],
          feeds: [],
        });
        break;
      }
      
      case 'night_wake':
        if (currentNightSleep?.isComplete === false) {
            currentNightSleep.wakeUps?.push({ time: eventTime, duration: event.duration || 0 });
        }
        break;

      case 'feed': {
        const activePeriod = currentNightSleep?.isComplete === false ? currentNightSleep : currentNap?.isComplete === false ? currentNap : null;
        if (activePeriod) {
            activePeriod.feeds?.push({ time: eventTime });
        }
        break;
      }
    }
  }

  // Finalize any incomplete periods at the end of the event list
  if (currentNightSleep?.isComplete === false && currentNightSleep.startTime && validEvents.length > 0) {
      let lastEventTime: Date;
      try {
        lastEventTime = validEvents[validEvents.length - 1].time.toDate();
        if (isNaN(lastEventTime.getTime())) {
          console.warn('Invalid last event time in timeline reconstruction');
          return periods; // Return early if we can't get a valid last event time
        }
      } catch (error) {
        console.warn('Error converting last event timestamp in timeline reconstruction:', error);
        return periods; // Return early if we can't convert the timestamp
      }
      
      // For incomplete night sleep, extend it to the end of the day it started on
      // or to the last event time, whichever is later
      const dayEnd = endOfDay(currentNightSleep.startTime);
      const finalEndTime = isAfter(lastEventTime, dayEnd) ? lastEventTime : dayEnd;

      const midnight = startOfDay(finalEndTime);
      if (currentNightSleep.startTime < midnight && isAfter(finalEndTime, midnight)) {
         const firstPart = { ...currentNightSleep, endTime: endOfDay(currentNightSleep.startTime), isComplete: false } as SleepPeriod;
         const secondPart = { ...currentNightSleep, startTime: startOfDay(finalEndTime), endTime: finalEndTime, isComplete: false } as SleepPeriod;
         periods.push(firstPart, secondPart);
      } else {
        periods.push({ ...currentNightSleep, endTime: finalEndTime, isComplete: false } as SleepPeriod);
      }
  }
   if (currentNap?.isComplete === false && validEvents.length > 0) {
      let lastEventTime: Date;
      try {
        lastEventTime = validEvents[validEvents.length - 1].time.toDate();
        if (isNaN(lastEventTime.getTime())) {
          console.warn('Invalid last event time in timeline reconstruction for nap');
          return periods; // Return early if we can't get a valid last event time
        }
      } catch (error) {
        console.warn('Error converting last event timestamp in timeline reconstruction for nap:', error);
        return periods; // Return early if we can't convert the timestamp
      }
      periods.push({ ...currentNap, endTime: lastEventTime, isComplete: false } as SleepPeriod);
  }

  return periods;
}
